// src/features/records/components/GalleryView/components/SkeletonGrid.js
import React, { useMemo } from 'react';
import styles from './SkeletonGrid.module.css';
import { useJustifiedGrid } from '../hooks';
import {
  resolveJustifiedRowHeight,
  JUSTIFIED_GRID_GAP,
  JUSTIFIED_GRID_MIN_SCALE,
  JUSTIFIED_GRID_MAX_SCALE,
} from '../layout/config';

// A handful of aspect ratios to mimic common media shapes (square, portrait, landscape).
const PLACEHOLDER_RATIOS = [1, 4 / 3, 3 / 4, 16 / 9, 9 / 16, 2];

function buildPlaceholderItems(count) {
  return Array.from({ length: count }, (_, index) => {
    const ratio = PLACEHOLDER_RATIOS[index % PLACEHOLDER_RATIOS.length];
    return {
      id: `skeleton-${index}`,
      aspectRatio: ratio,
    };
  });
}

const SkeletonGrid = ({ itemCount = 20 }) => {
  const placeholderItems = useMemo(() => buildPlaceholderItems(itemCount), [itemCount]);
  const { parentRef, containerWidth, rows, gap, rowVirtualizer } = useJustifiedGrid({
    items: placeholderItems,
    gap: JUSTIFIED_GRID_GAP,
    rowHeightForWidth: resolveJustifiedRowHeight,
    minScale: JUSTIFIED_GRID_MIN_SCALE,
    maxScale: JUSTIFIED_GRID_MAX_SCALE,
    justifyLastRow: false,
    widowLimit: 2,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalRows = rows.length;

  return (
    <div ref={parentRef} className={styles.scrollParent}>
      <div
        className={styles.gridContainer}
        style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}
      >
        {virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;

          const isLast = virtualRow.index === totalRows - 1;
          const rowSpacing = isLast ? 0 : gap;
          const templateColumns = row.items.map((item) => `${item.width}px`).join(' ');

          return (
            <div
              key={row.key}
              ref={rowVirtualizer.measureElement}
              data-index={virtualRow.index}
              className={styles.rowWrapper}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                height: row.height + rowSpacing,
                paddingBottom: rowSpacing,
              }}
            >
              <div
                className={styles.row}
                style={{
                  height: row.height,
                  width: row.justify
                    ? '100%'
                    : Math.min(row.rowWidth, containerWidth || row.rowWidth),
                  gridTemplateColumns: templateColumns,
                  columnGap: gap,
                }}
              >
                {row.items.map((cell) => (
                  <div
                    key={cell.key}
                    className={styles.skeletonItem}
                    style={{ width: cell.width, height: row.height }}
                  >
                    <div className={styles.shimmer} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SkeletonGrid;
